import { useState, type ReactNode } from "react";
import useSWRMutation from "swr/mutation";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { SignedAvatarImage } from "@/components/signed-avatar-image";
import { useToast } from "@/hooks/use-toast";
import { toastError } from "@/lib/toast";
import { putFetcher, uploadAssetFile } from "@/lib/swr";

export type BotProfile = {
  id: string;
  name?: string;
  avatarThumb?: string | null;
};

// Rename a bot / set its avatar (PRD cb72fb78). The avatar goes through the
// same POST /uploads as markdown images and is stored as the private-bucket
// reference; the server only accepts uploads/ references. `endpoint` is the
// caller's PUT route (app-scoped for App Settings, admin-scoped for the
// admin user page) — both take { name?, avatarThumb? }.
export const BotProfileDialog = ({
  bot,
  endpoint,
  onSaved,
  trigger,
}: {
  bot: BotProfile;
  endpoint: string;
  onSaved: () => void;
  trigger?: ReactNode;
}) => {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(bot.name ?? "");
  const [avatar, setAvatar] = useState<string | null>(bot.avatarThumb ?? null);
  const [uploading, setUploading] = useState(false);
  const { trigger: save, isMutating } = useSWRMutation(endpoint, putFetcher);

  const reset = () => {
    setName(bot.name ?? "");
    setAvatar(bot.avatarThumb ?? null);
  };

  const pick = async (file: File | undefined) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast({ title: "请选择图片文件", variant: "destructive" });
      return;
    }
    setUploading(true);
    try {
      const { url } = await uploadAssetFile(file);
      setAvatar(url);
    } catch (err) {
      toastError(toast, "上传失败", err);
    } finally {
      setUploading(false);
    }
  };

  const dirty =
    name.trim() !== (bot.name ?? "") || (avatar ?? null) !== (bot.avatarThumb ?? null);

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (o) reset();
      }}
    >
      <DialogTrigger asChild>
        {trigger ?? (
          <Button variant="outline" size="sm">
            Edit
          </Button>
        )}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit bot</DialogTitle>
          <DialogDescription>
            改名和头像。头像走站内附件上传,只对登录用户可见。
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="bot-profile-name">Name</Label>
            <Input
              id="bot-profile-name"
              value={name}
              maxLength={64}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="bot-profile-avatar">Avatar</Label>
            <div className="flex items-center gap-3">
              <Avatar className="h-12 w-12">
                <SignedAvatarImage src={avatar ?? undefined} alt={name} />
                <AvatarFallback>{name?.[0]}</AvatarFallback>
              </Avatar>
              <Input
                id="bot-profile-avatar"
                type="file"
                accept="image/*"
                disabled={uploading}
                className="max-w-xs"
                onChange={(e) => {
                  void pick(e.target.files?.[0]);
                  e.target.value = "";
                }}
              />
              {avatar && (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={uploading}
                  onClick={() => setAvatar(null)}
                >
                  Remove
                </Button>
              )}
            </div>
            {uploading && (
              <p className="text-xs text-muted-foreground">Uploading…</p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button
            disabled={isMutating || uploading || !dirty || !name.trim()}
            onClick={async () => {
              try {
                await save({ name: name.trim(), avatarThumb: avatar });
                setOpen(false);
                onSaved();
                toast({ title: "Bot updated" });
              } catch (err) {
                toastError(toast, "Failed to update bot", err);
              }
            }}
          >
            {isMutating ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BotProfileDialog;
